import { getBootstrapIconUrl, getBootstrapIconUrlByKey, BootstrapIcon } from '../../Global/icons'
import { tryLoadDependencies, tryLoadLightDOMStyles, WebAwesomeComponent } from '../../Global/loadDependency'
import {
    NavEnterpriseMenuItemAttr,
    NavEnterpriseMenuItemClass,
    NavEnterpriseMenuItemDefaults,
    NavEnterpriseMenuItemSelector,
    NavEnterpriseMenuItemTarget,
    NavEnterpriseMenuItemValues,
    type NavEnterpriseMenuItemModel,
} from './types/NavEnterpriseMenuItem.type'

const HomeIconUrl = getBootstrapIconUrl(BootstrapIcon.Home)
const LabelIconMap: Record<string, string> = {
    'my services': 'shield-lock',
    'billing & payment': 'wallet',
    claims: 'patch-exclamation',
    'resource center': 'collection',
}

class NavEnterpriseMenuItem extends HTMLElement {
    private linkElement: HTMLAnchorElement | null = null
    private iconElement: HTMLElement | null = null
    private labelElement: HTMLSpanElement | null = null
    private initialized = false

    private model: NavEnterpriseMenuItemModel = {
        href: NavEnterpriseMenuItemDefaults.href,
        label: NavEnterpriseMenuItemDefaults.label,
        icon: NavEnterpriseMenuItemDefaults.icon,
        active: NavEnterpriseMenuItemDefaults.active,
        target: NavEnterpriseMenuItemDefaults.target,
        ariaLabel: NavEnterpriseMenuItemDefaults.ariaLabel,
    }

    constructor() {
        super()
        tryLoadDependencies(document, [WebAwesomeComponent.Icon])
        tryLoadLightDOMStyles('NavEnterpriseMenuItem.css')
    }

    connectedCallback() {
        document.AscendAwesome?.LogManager?.logDebug('NavEnterpriseMenuItem connectedCallback')

        if (!this.initialized) {
            this.renderTemplate()
            this.captureDomReferences()
            // One-time read of HTML attributes so plain-HTML usage works (no observedAttributes needed)
            const href = this.getAttribute(NavEnterpriseMenuItemAttr.HREF)
            if (href) this.model.href = href
            const label = this.getAttribute(NavEnterpriseMenuItemAttr.LABEL)
            if (label !== null) this.model.label = label
            const icon = this.getAttribute(NavEnterpriseMenuItemAttr.ICON)
            if (icon) this.model.icon = icon
            const active = this.getAttribute(NavEnterpriseMenuItemAttr.ACTIVE)
            if (active !== null) this.model.active = active !== 'false'
            const target = this.getAttribute(NavEnterpriseMenuItemAttr.TARGET)
            if (target) this.model.target = target
            const ariaLabel = this.getAttribute(NavEnterpriseMenuItemAttr.ARIA_LABEL)
            if (ariaLabel) this.model.ariaLabel = ariaLabel
            this.initialized = true
        }

        this.render()
    }

    get href() {
        return this.model.href
    }

    set href(value: string) {
        this.model.href = value?.trim() || NavEnterpriseMenuItemDefaults.href
        this.render()
    }

    get label() {
        return this.model.label
    }

    set label(value: string) {
        this.model.label = value?.trim() || ''
        this.render()
    }

    get icon() {
        return this.model.icon
    }

    set icon(value: string) {
        this.model.icon = value?.trim() || NavEnterpriseMenuItemDefaults.icon
        this.render()
    }

    get active() {
        return this.model.active
    }

    set active(value: boolean) {
        this.model.active = Boolean(value)
        this.render()
    }

    get target() {
        return this.model.target
    }

    set target(value: string) {
        this.model.target = value?.trim() || NavEnterpriseMenuItemDefaults.target
        this.render()
    }

    get ariaLabel() {
        return this.model.ariaLabel
    }

    set ariaLabel(value: string) {
        this.model.ariaLabel = value?.trim() || NavEnterpriseMenuItemDefaults.ariaLabel
        this.render()
    }

    private renderTemplate() {
        this.innerHTML = `
      <a class="${NavEnterpriseMenuItemClass.MENU_ITEM}" href="${NavEnterpriseMenuItemDefaults.href}">
        <wa-icon aria-hidden="${NavEnterpriseMenuItemValues.ARIA_HIDDEN_TRUE}"></wa-icon>
        <span class="${NavEnterpriseMenuItemClass.LABEL}"></span>
      </a>
    `
    }

    private captureDomReferences() {
        this.linkElement = this.querySelector(NavEnterpriseMenuItemSelector.ANCHOR)
        this.iconElement = this.querySelector(NavEnterpriseMenuItemSelector.ICON)
        this.labelElement = this.querySelector(NavEnterpriseMenuItemSelector.LABEL)
    }

    private resolveIconUrl(iconName: string) {
        try {
            return getBootstrapIconUrlByKey(iconName)
        } catch {
            return HomeIconUrl
        }
    }

    private getInferredIconName(label: string) {
        const key = label.trim().toLowerCase()
        return LabelIconMap[key] || 'house'
    }

    private render() {
        if (!this.initialized || !this.linkElement || !this.iconElement || !this.labelElement) return

        const label = this.model.label
        this.linkElement.href = this.model.href || NavEnterpriseMenuItemDefaults.href
        this.labelElement.textContent = label
        const iconName = this.model.icon || this.getInferredIconName(label)
        const iconUrl = this.resolveIconUrl(iconName)
        this.iconElement.setAttribute('src', iconUrl)

        const isActive = Boolean(this.model.active)
        this.linkElement.classList.toggle(NavEnterpriseMenuItemClass.ACTIVE, isActive)
        this.linkElement.setAttribute(NavEnterpriseMenuItemAttr.ARIA_CURRENT, isActive ? NavEnterpriseMenuItemValues.ARIA_CURRENT_PAGE : NavEnterpriseMenuItemValues.ARIA_CURRENT_FALSE)

        const target = this.model.target || NavEnterpriseMenuItemDefaults.target
        if (target !== NavEnterpriseMenuItemTarget.SELF) {
            this.linkElement.setAttribute(NavEnterpriseMenuItemAttr.TARGET, target)
            if (target === NavEnterpriseMenuItemTarget.BLANK) {
                this.linkElement.setAttribute(NavEnterpriseMenuItemAttr.REL, NavEnterpriseMenuItemValues.REL_NOOPENER)
            } else {
                this.linkElement.removeAttribute(NavEnterpriseMenuItemAttr.REL)
            }
        } else {
            this.linkElement.removeAttribute(NavEnterpriseMenuItemAttr.TARGET)
            this.linkElement.removeAttribute(NavEnterpriseMenuItemAttr.REL)
        }

        const ariaLabel = this.model.ariaLabel || ''
        if (ariaLabel) {
            this.linkElement.setAttribute(NavEnterpriseMenuItemAttr.ARIA_LABEL, ariaLabel)
        } else {
            this.linkElement.removeAttribute(NavEnterpriseMenuItemAttr.ARIA_LABEL)
        }
    }
}

if (!customElements.get('ts-nav-enterprise-menu-item')) {
    customElements.define('ts-nav-enterprise-menu-item', NavEnterpriseMenuItem)
}
export { NavEnterpriseMenuItem }
